import { useQuery } from '@tanstack/react-query'
import { api, type ReviewConfidence } from '@/api/client'

// Confidence scores are precomputed proactively by the Airbnb
// review-window-open webhook (admin-api's uplisting-review-webhook.ts), not
// generated on demand the way a guest-review draft is — there is no
// client-triggered regenerate endpoint. `queued` only shows up in the brief
// window right after that webhook fires and before the queue consumer has
// written a result, so this polls a handful of times and then gives up
// rather than adopting suggested-review-block.tsx's full target-version
// machinery, which exists specifically to track a *locally triggered*
// generation this feature has no equivalent of.
const POLL_INTERVAL_MS = 3000
const MAX_QUEUED_POLL_ATTEMPTS = 5

export type ConfidenceTier = 'high' | 'mid' | 'low'

export function getConfidenceTier(score: number): ConfidenceTier {
  if (score >= 80) return 'high'
  if (score >= 45) return 'mid'
  return 'low'
}

export const CONFIDENCE_TIER_LABEL: Record<ConfidenceTier, string> = {
  high: 'Nothing to see',
  mid: 'Worth a look',
  low: "Reach out before it's public",
}

/**
 * Fetches (and lightly polls while queued) the precomputed five-star
 * confidence score for a reservation. Multiple components can call this for
 * the same reservationId — e.g. the collapsed pill and its expanded detail
 * panel both need it — and share one cached fetch via TanStack Query's
 * queryKey dedup, the same way this app already relies on the cache for
 * suggested-review-block.tsx.
 *
 * `enabled: false` reads the cache without asking the network: the /reviews
 * rows get their pill from the digest and only fetch here for a still-queued
 * score or for the signals behind an expanded one (CAP-744).
 */
export function useReviewConfidence(
  reservationId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const query = useQuery({
    queryKey: ['review-confidence', reservationId],
    queryFn: () => api.getReviewConfidence(reservationId as string),
    enabled: !!reservationId && enabled,
    // The expanded row's wrapper and its panel both observe this key; the
    // panel mounting a moment after the fetch must not trigger a second one.
    // Only a completed record is treated as fresh: a cached "no record" or
    // queued answer (e.g. from another page a moment before scoring
    // finished) must be asked again when the digest says there is a score.
    staleTime: query => (query.state.data?.confidence?.status === 'completed' ? 10_000 : 0),
    refetchInterval: query => {
      const attempts = query.state.dataUpdateCount + query.state.errorUpdateCount
      if (
        query.state.data?.confidence?.status === 'queued' &&
        attempts < MAX_QUEUED_POLL_ATTEMPTS
      ) {
        return POLL_INTERVAL_MS
      }
      return false
    },
  })

  const confidence: ReviewConfidence | null = query.data?.confidence ?? null

  return {
    confidence,
    recommendation: query.data?.recommendation ?? null,
    propertyPrior: query.data?.property_prior ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    isQueued: confidence?.status === 'queued',
  }
}
